import { Controller } from "@hotwired/stimulus"

/**
 * ET Instrument Simple Controller
 * Simple dropdown selector for ET Instrument (single selection)
 * Auto-populates maker and serial number when instrument is selected
 */
export default class extends Controller {
  static targets = ["select", "maker", "serialNo", "hidden"]

  connect() {
    console.log("[et-instrument-simple] Controller connected")

    // Handle select2 events if jQuery and select2 are available
    if (window.jQuery && this.hasSelectTarget) {
      const $select = window.jQuery(this.selectTarget)

      // Listen to select2:select event for when an option is chosen
      $select.on('select2:select', (e) => {
        this.handleSelection(e.params.data.id)
      })

      // Listen to select2:clear event for when selection is cleared
      $select.on('select2:clear', () => {
        this.clearFields()
        this.updateHiddenField(null)
      })
    }
  }

  disconnect() {
    // Clean up select2 event listeners
    if (window.jQuery && this.hasSelectTarget) {
      window.jQuery(this.selectTarget).off('select2:select select2:clear')
    }
  }

  // Called from Stimulus action (native change event)
  async onChange(event) {
    const equipmentId = event.target.value
    await this.handleSelection(equipmentId)
  }

  // Unified handler for both native change and select2 events
  async handleSelection(equipmentId) {
    if (!equipmentId) {
      // Clear fields if no instrument selected
      this.clearFields()
      this.updateHiddenField(null)
      return
    }

    try {
      const response = await fetch(`/equipment/${equipmentId}.json`)
      if (!response.ok) throw new Error('Failed to fetch equipment data')

      const data = await response.json()

      // Update display fields
      if (this.hasMakerTarget) {
        this.makerTarget.value = data.maker || ''
      }
      if (this.hasSerialNoTarget) {
        this.serialNoTarget.value = data.serial_no || 'Missing serial number'
      }

      // Update hidden field for form submission
      this.updateHiddenField(equipmentId)

      // Dispatch event for ET auto-populate (method specific variables)
      document.dispatchEvent(new CustomEvent('equipment:added', {
        detail: {
          equipmentId: equipmentId,
          equipmentTypeName: 'ET Instrument',
          equipmentData: data
        }
      }))

      // Trigger autosave
      this.triggerAutosave()

    } catch (error) {
      console.error("[et-instrument-simple] Error:", error)
    }
  }

  clearFields() {
    if (this.hasMakerTarget) this.makerTarget.value = ''
    if (this.hasSerialNoTarget) this.serialNoTarget.value = ''
  }

  updateHiddenField(equipmentId) {
    if (!this.hasHiddenTarget) return

    // Get the reference block's currently selected value to preserve it
    const referenceSelect = document.querySelector('[data-et-reference-simple-target="select"]')
    const referenceId = referenceSelect ? referenceSelect.value : null

    // Build combined hidden fields for both equipment types
    let hiddenFields = ''

    if (equipmentId) {
      hiddenFields += `<input type="hidden" name="report[equipment_ids][]" value="${equipmentId}" />\n`
    }

    if (referenceId) {
      hiddenFields += `<input type="hidden" name="report[equipment_ids][]" value="${referenceId}" />`
    }

    this.hiddenTarget.innerHTML = hiddenFields

    // Also update the reference controller's hidden field to be empty (we're managing both here)
    const referenceHiddenContainer = document.querySelector('[data-et-reference-simple-target="hidden"]')
    if (referenceHiddenContainer) {
      referenceHiddenContainer.innerHTML = ''
    }
  }

  triggerAutosave() {
    const changeEvent = new Event('change', { bubbles: true })
    this.element.dispatchEvent(changeEvent)
  }
}
